import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";

import AppModal from "@shared/ui/Modal";
import Button from "@shared/ui/Button";
import { Input } from "@shared/ui/Inputs";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";

import { CREATE_CONNECTION_REQUEST_MUTATION } from "~/api/_mutations";
import { RequestAccessFormValues, RequestAccessModalProps } from "./types";

const emailPattern =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const RequestAccessModal = ({
  isOpen = false,
  oemId,
  onClose,
  onLoginClick,
  onSuccess,
}: RequestAccessModalProps) => {
  const { messages: msg } = useIntl();

  const [createConnectionRequest, { loading }] = useMutation(
    CREATE_CONNECTION_REQUEST_MUTATION,
  );

  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors },
  } = useForm<RequestAccessFormValues>({
    defaultValues: {
      fullName: "",
      email: "",
      companyName: "",
    },
  });

  const onSubmit = async (data: RequestAccessFormValues) => {
    try {
      await createConnectionRequest({
        variables: {
          input: {
            name: data.fullName,
            email: data.email,
            company: data.companyName,
            sentTo: oemId,
          },
        },
      });
      toast(
        <Toast
          message={msg.customerPortals["requestSuccess"] as string}
          type={TOAST_TYPES.SUCCESS}
        />,
        { closeButton: false },
      );
      reset({ fullName: "", email: "", companyName: "" });
      onSuccess?.();
      onClose?.();
    } catch (error) {
      toast(
        <Toast
          message={
            (error as Error)?.message ||
            (msg.customerPortals["requestFailed"] as string)
          }
          type={TOAST_TYPES.ERROR}
        />,
        { closeButton: false },
      );
      setError("root.random", {
        type: "random",
        message: (error as Error)?.message,
      });
    }
  };

  const onError = () => {};

  const fullNameField = register("fullName", {
    required: {
      value: true,
      message: msg?.common["errors"]["fullNameRequired"],
    },
  });

  const emailField = register("email", {
    required: {
      value: true,
      message: msg?.common["errors"]["emailRequired"],
    },
    pattern: {
      value: emailPattern,
      message: msg?.login["email"]["invalidEmail"],
    },
  });

  const companyNameField = register("companyName");

  const resetForm = useCallback(() => {
    reset({ fullName: "", email: "", companyName: "" });
  }, [reset]);

  return (
    <AppModal
      isOpen={isOpen}
      title={msg.customerPortals["welcome"]["requestPortalAccess"] as string}
      handleClose={
        loading
          ? undefined
          : () => {
              resetForm();
              onClose?.();
            }
      }
      handleSubmit={handleSubmit(onSubmit, onError)}
      maxWidth="!max-w-[400px]"
      disableUpdate={loading}
      disableCancel={loading}
      updateButtonText={msg?.common?.["submit"] || "Submit"}
      content={
        <div className="flex flex-col gap-lg">
          <BodyText
            size={BODY_TEXT_SIZES.SMALL}
            color="text-secondary"
            className="mb-2"
          >
            {msg.customerPortals["welcome"]["requestFormDescription"]}
          </BodyText>
          <Input
            keyId="fullName"
            label={`${msg["common"]["signUp"]["fullName"]["label"]} *`}
            name="fullName"
            type="text"
            placeholder={msg["common"]["signUp"]["fullName"]["placeholder"]}
            {...fullNameField}
            setRef={fullNameField.ref}
            errorMessage={errors?.fullName?.message?.toString()}
            maxLength={50}
            customInputClasses={
              errors?.fullName?.message
                ? "bg-primary border-danger focus:bg-primary focus:border-danger"
                : watch("fullName")
                ? "bg-primary border-primary"
                : "bg-accent border-transparent"
            }
          />

          <Input
            keyId="email"
            label={`${msg.login["email"]["label"]}`}
            name="email"
            type="text"
            placeholder={msg.login["email"]["placeholder"]}
            {...emailField}
            setRef={emailField.ref}
            errorMessage={errors?.email?.message?.toString()}
            maxLength={254}
            customInputClasses={
              errors?.email?.message
                ? "bg-primary border-danger focus:bg-primary focus:border-danger"
                : watch("email")
                ? "bg-primary border-primary"
                : "bg-accent border-transparent"
            }
          />

          <Input
            keyId="companyName"
            label={msg["common"]["signUp"]["companyName"]["label"]}
            name="companyName"
            type="text"
            placeholder={msg["common"]["signUp"]["companyName"]["placeholder"]}
            {...companyNameField}
            setRef={companyNameField.ref}
            errorMessage={errors?.companyName?.message?.toString()}
            maxLength={50}
            customInputClasses={
              errors?.companyName?.message
                ? "bg-primary border-danger focus:bg-primary focus:border-danger"
                : watch("companyName")
                ? "bg-primary border-primary"
                : "bg-accent border-transparent"
            }
          />
        </div>
      }
      footer={
        <div className="px-6 pb-6">
          <Button
            fullWidth={true}
            text={msg.customerPortals["welcome"]["submitRequest"] as string}
            className="mb-4"
            onClick={handleSubmit(onSubmit, onError)}
          />
          <div className="flex justify-center">
            <BodyText color="text-secondary">
              {msg.customerPortals["welcome"]["alreadyHaveAccount"]}
            </BodyText>{" "}
            <BodyText
              onClick={onLoginClick}
              color="text-brand"
              className="!font-bold ml-1 cursor-pointer"
            >
              {msg.customerPortals["welcome"]["login"]}
            </BodyText>
          </div>
        </div>
      }
    />
  );
};
